/**
 * API Configuration Utility
 * Handles dynamic API base URL detection for different deployment scenarios
 */

export interface ApiConfig {
  baseUrl: string;
  isLocalhost: boolean;
  isDevelopment: boolean;
}

/**
 * Get the appropriate API base URL based on current environment
 */
export const getApiConfig = (): ApiConfig => {
  // If environment variable is explicitly set and not empty, use it
  if (process.env.REACT_APP_RAG_API_BASE && process.env.REACT_APP_RAG_API_BASE.trim() !== '') {
    return {
      baseUrl: process.env.REACT_APP_RAG_API_BASE,
      isLocalhost: process.env.REACT_APP_RAG_API_BASE.includes('localhost'),
      isDevelopment: process.env.NODE_ENV === 'development'
    };
  }
  
  // Get current location info
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  const port = window.location.port;
  
  // Determine if we're in development mode
  const isDevelopment = process.env.NODE_ENV === 'development';
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  
  // Construct API URL based on current context
  let apiBaseUrl: string;
  
  // For external access (like rag-v3.api.dev.dxr.kr), use the same hostname and port
  // This ensures HTTPS requests work properly and avoid mixed content errors
  if (port) {
    // If there's a port, use it (for local development or specific port configurations)
    apiBaseUrl = `${protocol}//${hostname}:${port}`;
  } else {
    // If no port, use the same hostname (for standard HTTPS/HTTP ports)
    apiBaseUrl = `${protocol}//${hostname}`;
  }
  
  return {
    baseUrl: apiBaseUrl,
    isLocalhost,
    isDevelopment
  };
};

/**
 * Get API base URL (simple version for backward compatibility)
 */
export const getApiBaseUrl = (): string => {
  return getApiConfig().baseUrl;
};

/**
 * Check if the current API configuration is valid
 */
export const validateApiConfig = (config: ApiConfig): boolean => {
  try {
    new URL(config.baseUrl);
    return true;
  } catch {
    return false;
  }
};

/**
 * Get API configuration with validation
 */
export const getValidatedApiConfig = (): ApiConfig => {
  const config = getApiConfig();
  
  if (!validateApiConfig(config)) {
    // Fallback to current hostname with same port (Nginx proxy)
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    const port = window.location.port;
    
    let fallbackUrl: string;
    if (port) {
      fallbackUrl = `${protocol}//${hostname}:${port}`;
    } else {
      fallbackUrl = `${protocol}//${hostname}`;
    }
    
    return {
      baseUrl: fallbackUrl,
      isLocalhost: hostname === 'localhost' || hostname === '127.0.0.1',
      isDevelopment: process.env.NODE_ENV === 'development'
    };
  }
  
  return config;
};
